import React, { useEffect, useRef, useState } from "react";
import { Session, API, FoodEntry, FoodItem, SevenDayStats } from "./api.tsx";

const Login = (props: { setSession: (_: Session) => void; api: API }) => {
  const setSession: (_: Session) => void = props.setSession;
  const api: API = props.api;
  const [state, setState] = useState<"initial" | "working" | "failed">(
    "initial",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const passwordInput = useRef<HTMLInputElement>(null);
  const login = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setState("working");
    try {
      const session = await api.login(email, password);
      setSession(session);
    } catch (e) {
      setState("failed");
      setPassword("");
      passwordInput.current?.focus();
    }
  };
  return (
    <form className="login-form">
      <div>
        Username:
        <input
          type="email"
          disabled={state === "working" || undefined}
          value={email}
          autoFocus={true}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        Password:
        <input
          type="password"
          disabled={state === "working" || undefined}
          value={password}
          ref={passwordInput}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div>
        <button disabled={state === "working" || undefined} onClick={login}>
          Login
        </button>
      </div>
      {state === "failed" ? (
        <div className="error">Wrong username or password!</div>
      ) : (
        ""
      )}
    </form>
  );
};

const FoodItemInput = (props: {
  foodItems: Array<FoodItem>;
  onSelectFoodItem: (fi: FoodItem) => Promise<void>;
}) => {
  const [text, setText] = useState("");
  const [selected, setSelected] = useState<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const onSelectFoodItem = async (fi: FoodItem) => {
    await props.onSelectFoodItem(fi);
    setSelected(undefined);
    setText("");
    inputRef.current?.focus();
  };

  const onKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        if (selected === undefined) {
          setSelected(filteredFoodItems.length - 1);
        } else if (selected > 0) {
          setSelected(selected - 1);
        } else {
          setSelected(filteredFoodItems.length - 1);
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (selected === undefined) {
          setSelected(0);
        } else if (selected < filteredFoodItems.length - 1) {
          setSelected(selected + 1);
        } else {
          setSelected(0);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (selected !== undefined) {
          await onSelectFoodItem(filteredFoodItems[selected]);
        }
        break;
    }
  };

  let filteredFoodItems: Array<FoodItem> = [];
  let foodItemElements: Array<React.ReactElement> = [];
  if (text.length >= 2) {
    const lowercaseMatchText = text.toLowerCase();
    filteredFoodItems = props.foodItems.filter((fi) =>
      fi.name.toLowerCase().includes(lowercaseMatchText),
    );
    foodItemElements = filteredFoodItems.map((fi, ix) => {
      const lowerCaseName = fi.name.toLowerCase();
      const filterTextIndex = lowerCaseName.indexOf(lowercaseMatchText);
      const pre = fi.name.substring(0, filterTextIndex);
      const match = fi.name.substring(
        filterTextIndex,
        filterTextIndex + text.length,
      );
      const post = fi.name.substring(filterTextIndex + text.length);
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid, no-script-url
        <span className="link" key={fi.id}>
          <li
            className={"food-item" + (ix === selected ? " selected" : "")}
            onClick={(e) => onSelectFoodItem(fi)}
          >
            <span className="name">
              {pre}
              <span className="text-match">{match}</span>
              {post}
            </span>
            <span className="extra">({fi.kcal} kcal)</span>
          </li>
        </span>
      );
    });
  }
  return (
    <div className="food-item-input">
      <ul className="food-item-list">{foodItemElements}</ul>
      <input
        placeholder="Lägg till mål"
        type="text"
        value={text}
        ref={inputRef}
        onKeyDown={onKeyPress}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
};

const FoodEntryList = ({ onUpdateEntry, foodItems, foodEntries }) => {
  const kcal = foodEntries
    .map((fe: FoodEntry) => fe.count * foodItems[fe.food_item_id].kcal)
    .reduce((a: number, x: number) => a + x, 0);
  const updateCount = async (foodEntry: FoodEntry, count: number) => {
    foodEntry.count = count;
    onUpdateEntry(foodEntry);
  };
  return (
    <div className="food-entry-list">
      <h2>
        Tidigare mål <span className="extra">({kcal} kcal)</span>
      </h2>
      <ul>
        {foodEntries.map((fe: FoodEntry) => {
          const foodItem = foodItems[fe.food_item_id];
          const kcal = fe.count * foodItem.kcal;
          const date = new Date(fe.timestamp * 1000);
          const hourStr =
            date.getHours() < 10
              ? "0" + date.getHours()
              : date.getHours().toString();
          const minuteStr =
            date.getMinutes() < 10
              ? "0" + date.getMinutes()
              : date.getMinutes().toString();
          const timeStr = `${hourStr}:${minuteStr}`;
          return (
            <li className="food-entry" key={fe.id}>
              <CounterWidget
                initialValue={fe.count}
                onChange={(value) => updateCount(fe, value)}
              />
              <span className="timestamp">{timeStr}</span>
              <span className="name">{foodItem.name}</span>
              <span className="extra">
                ({fe.count} st., {kcal} kcal)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CounterWidget = (props: {
  initialValue: number;
  onChange: (value: number) => Promise<void>;
}) => {
  const [count, setCount] = useState(props.initialValue);
  const onChange = async (value: number) => {
    await props.onChange(value);
    setCount(value);
  };
  return (
    <span style={{ float: "right" }}>
      <button
        className="link"
        style={{ marginRight: "0.5em" }}
        onClick={() => onChange(count - 1)}
      >
        -
      </button>{" "}
      {count}{" "}
      <button
        style={{ marginLeft: "0.5em" }}
        className="link"
        onClick={() => onChange(count + 1)}
      >
        +
      </button>
    </span>
  );
};

const SevenDayStatsWidget = (props: {
  weight?: number;
  weight_diff?: number;
  kcal?: number;
  kcal_diff?: number;
}) => {
  return (
    <ul className="seven-day-stats">
      {props.weight !== null ? <li className="label">Seven day avg.</li> : ""}
      {props.weight_diff !== null ? <li className="label">Weekly diff</li> : ""}
      {props.weight !== null ? <li>{props.weight} kg</li> : ""}
      {props.weight_diff !== null ? <li>{props.weight_diff} kg</li> : ""}
      {props.kcal !== null ? <li className="label">Seven day avg.</li> : ""}
      {props.kcal_diff !== null ? <li className="label">Weekly diff</li> : ""}
      {props.kcal !== null ? <li>{props.kcal} kcal</li> : ""}
      {props.kcal_diff !== null ? <li>{props.kcal_diff} kcal</li> : ""}
    </ul>
  );
};

const Dashboard = (props: { api: API }) => {
  const [weightAlreadyEntered, setWeightAlreadyEntered] =
    useState<boolean>(true);
  const [showWeightEntryDialog, setShowWeightEntryDialog] =
    useState<boolean>(false);
  const [stats, setStats] = useState<SevenDayStats | undefined>(undefined);
  const [foodEntries, setFoodEntries] = useState<Array<FoodEntry>>([]);
  const [foodItems, setFoodItems] = useState<{
    dict: { [id: number]: FoodItem };
    array: Array<FoodItem>;
  }>({ dict: {}, array: [] });
  const updateWeight = async (kilos: number) => {
    setShowWeightEntryDialog(false);
    setWeightAlreadyEntered(true);
    const weightEntry = {
      kilos: kilos,
      timestamp: Math.floor(new Date().getTime() / 1000),
    };

    await props.api.addWeightEntry(weightEntry);
    const updatedStats = await props.api.getWeeklyStats(new Date());
    setStats(updatedStats);
  };
  const addFoodEntry = async (foodItem: FoodItem) => {
    const newFoodEntry = {
      food_item_id: foodItem.id,
      count: 1,
      timestamp: Math.floor(new Date().getTime() / 1000),
    };
    const foodEntry = await props.api.addFoodEntry(newFoodEntry);
    setFoodEntries((foodEntries) => [...foodEntries, foodEntry]);
  };
  const updateFoodEntry = async (foodEntry: FoodEntry) => {
    let newFoodEntries = foodEntries;
    if (foodEntry.count === 0) {
      await props.api.deleteFoodEntry(foodEntry.id);
      newFoodEntries = foodEntries.filter((e) => e.id !== foodEntry.id);
    } else {
      await props.api.updateFoodEntry(foodEntry);
    }
    setFoodEntries([...newFoodEntries]);
  };

  useEffect(() => {
    (async () => {
      const foodItems = await props.api.getFoodItems();
      const foodItemList: Array<FoodItem> = [];
      for (const id in foodItems) {
        foodItemList.push(foodItems[id]);
      }
      const today = new Date();
      const foodEntries = await props.api.getFoodEntries(today);
      const todays_weight_entries = await props.api.getWeightEntries(today);
      if (!todays_weight_entries.length) {
        setWeightAlreadyEntered(false);
      }
      const stats = await props.api.getWeeklyStats(today);
      setStats(stats);
      setFoodItems({ dict: foodItems, array: foodItemList });
      setFoodEntries(foodEntries);
    })();
  }, [props.api]);

  return (
    <div className="dashboard">
      {weightAlreadyEntered ? (
        ""
      ) : showWeightEntryDialog ? (
        <WeightInput
          averageWeight={stats?.avg_weight || undefined}
          onCancel={() => setShowWeightEntryDialog(false)}
          onSubmit={updateWeight}
        />
      ) : (
        <button
          className="extra"
          onClick={() => setShowWeightEntryDialog(true)}
        >
          Registrera vikt
        </button>
      )}
      <FoodEntryList
        onUpdateEntry={updateFoodEntry}
        foodItems={foodItems.dict}
        foodEntries={foodEntries}
      />
      <SevenDayStatsWidget
        weight={stats?.avg_weight}
        weight_diff={stats?.weight_diff}
        kcal={stats?.avg_kcal}
        kcal_diff={stats?.kcal_diff}
      />
      <FoodItemInput
        foodItems={foodItems.array}
        onSelectFoodItem={addFoodEntry}
      />
    </div>
  );
};

const WeightInput = (props: {
  averageWeight: number | undefined;
  onSubmit: (weight: number) => void;
  onCancel: () => void;
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [kilos, setKilos] = useState<number | undefined>(undefined);
  const onChange = (kilos: number) => {
    if (props.averageWeight !== undefined) {
      if (kilos > props.averageWeight + 10.0) {
        setError("Den angivna vikten är misstänkt hög.");
      } else if (kilos < props.averageWeight - 10) {
        setError("Den angivna vikten är misstänkt låg.");
      } else {
        setError(undefined);
      }
    }
    setKilos(kilos);
  };
  const submit = async () => {
    if (!kilos) {
      props.onCancel();
    } else {
      props.onSubmit(Number(kilos));
    }
  };
  return (
    <form className="weight-input" onSubmit={submit}>
      <input
        type="number"
        step=".01"
        placeholder="Vikt i kilo"
        autoFocus={true}
        value={kilos}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <button>Registrera vikt</button>
      {error ? (
        <span
          className="error"
          style={{ display: "block", textAlign: "center", padding: "0.5em" }}
        >
          {error}
        </span>
      ) : (
        ""
      )}
    </form>
  );
};

const App = (props: { api: API }) => {
  const [session, setSession] = useState<Session | null>(
    props.api.getActiveSession(),
  );
  return (
    <div className="App">
      {session ? (
        <Dashboard api={props.api} />
      ) : (
        <Login setSession={setSession} api={props.api} />
      )}
    </div>
  );
};

export default App;
